import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { DynamicAutocompleteComponent } from 'src/app/base/shared/dynamic-autocomplete/dynamic-autocomplete.component';
import { IDynamicAutocompleteItem } from 'src/app/base/shared/dynamic-autocomplete/dynamic-autocomplete-item';
import { FilterDTO, FilterOperator, SortDirection, VehicleListDTO } from 'src/app/base/models/models.swagger.generated';
import { SamplingProfilesService } from '../../sampling-profiles/sampling-profiles.service';
import { SamplingProfileLookupService } from '../sampling-profile-lookup.service';

/**
 * Every vehicle always has a profile (the ingestion service auto-assigns "Standard"), so this dialog
 * is always a reassignment — there is no "unassign" action.
 */
@Component({
  selector: 'app-assign-profile-dialog',
  templateUrl: './assign-profile-dialog.component.html',
  styleUrls: ['./assign-profile-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DialogModule, DynamicAutocompleteComponent, ReactiveFormsModule, TranslatePipe],
})
export class AssignProfileDialogComponent implements OnChanges {

  @Input() public vehicle: VehicleListDTO | null = null;
  @Input() public visible = false;
  @Output() public readonly visibleChange = new EventEmitter<boolean>();

  /** Raised after a successful assignment so the parent grid can refresh. */
  @Output() public readonly assigned = new EventEmitter<void>();

  public readonly profileControl = new FormControl<number | null>(null);
  public assigning = false;
  public initialProfileItem: IDynamicAutocompleteItem | null = null;

  constructor(
    private samplingProfilesService: SamplingProfilesService,
    private samplingProfileLookupService: SamplingProfileLookupService,
    private translateService: TranslateService,
  ) {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicle'] || changes['visible']) {
      this.initialProfileItem = this.vehicle?.samplingProfileId
        ? { id: this.vehicle.samplingProfileId, name: this.vehicle.samplingProfileName ?? `#${this.vehicle.samplingProfileId}` }
        : null;
      this.profileControl.setValue(this.vehicle?.samplingProfileId ?? null, { emitEvent: false });
    }
  }

  public get dialogTitle(): string {
    return this.translateService.instant('i18n.domain.vehicles.assign-dialog.title', { param: this.vehicle?.vin ?? this.vehicle?.model ?? '' });
  }

  public get canAssign(): boolean {
    return !this.assigning && !!this.profileControl.value && this.profileControl.value !== this.vehicle?.samplingProfileId;
  }

  public searchProfiles = (searchText: string): Observable<IDynamicAutocompleteItem[]> => {
    const filters: FilterDTO[] = searchText
      ? [new FilterDTO({ isComplex: false, field: 'Name', operator: FilterOperator.Contains, value: searchText })]
      : [];

    return this.samplingProfileLookupService.getData(0, 20, filters, 'Name', SortDirection.Ascending).pipe(
      map(result => (result?.items ?? []).map(profile => ({ id: profile.id!, name: profile.name! } as IDynamicAutocompleteItem))),
    );
  };

  public onAssign(): void {
    if (!this.canAssign || !this.vehicle?.id || !this.profileControl.value) {
      return;
    }

    this.assigning = true;
    this.samplingProfilesService.assignProfileToVehicle(this.vehicle.id, this.profileControl.value).subscribe({
      next: () => {
        this.assigning = false;
        this.assigned.emit();
        this.close();
      },
      error: () => {
        this.assigning = false;
      },
    });
  }

  public close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
